import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { IHttpRequestOptions, IHttpClientRequest } from '../models/interfaces/requests';

export class BaseHttpClient {
    private errorHandler: ((err: { message: string }) => void) | null;
    private controller: AbortController;
    private client: AxiosInstance;

    constructor(baseUrl: string) {
        this.errorHandler = null;
        this.controller = new AbortController();
        const clientUrl = baseUrl ? process.env.REACT_APP_API_URI + baseUrl : process.env.REACT_APP_API_URI;
        this.client = axios.create({
            baseURL: clientUrl,
            signal: this.controller.signal,
        });
    }

    public abort = (): void => {
        this.controller.abort();
        this.controller = new AbortController();
    };

    public setErrorHandler = (errorHandler: (err: { message: string }) => void): void => {
        this.errorHandler = errorHandler;
    };

    private handleError = (err: { message: string }): void => {
        if (this.errorHandler) {
            this.errorHandler(err);
        }
    };

    public get<T>(request: IHttpClientRequest<T>, options: IHttpRequestOptions = { abortable: true }): Promise<T> {
        const { abortable } = options;
        return new Promise((resolve, reject) => {
            const { url, params, headers } = request ?? {};
            const config: AxiosRequestConfig = {
                headers: { ...headers },
                params: params,
            };

            if (abortable) {
                config.signal = this.controller.signal;
            }

            const requestUrl = url ? url : '';
            this.client
                .get(requestUrl, config)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((reason) => {
                    this.handleError(reason);
                    console.error(reason);
                    reject(reason);
                });
        });
    }

    public post<K, V>(request: IHttpClientRequest<V>, options: IHttpRequestOptions = { abortable: true }): Promise<K> {
        const { abortable } = options;
        return new Promise((resolve, reject) => {
            const { url, params, headers, payload } = request ?? {};
            const config: AxiosRequestConfig = {
                headers: { ...headers },
                params: params,
            };

            if (abortable) {
                config.signal = this.controller.signal;
            }

            const requestUrl = url ? url : '';
            this.client
                .post(requestUrl, payload, config)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((reason) => {
                    this.handleError(reason);
                    console.error(reason);
                    reject(reason);
                });
        });
    }

    public delete<T>(request: IHttpClientRequest<T>): Promise<void> {
        return new Promise((resolve, reject) => {
            const { url, params, headers } = request ?? {};
            const config: AxiosRequestConfig = {
                headers: { ...headers },
                params: params,
            };

            const requestUrl = url ? url : '';
            this.client
                .delete(requestUrl, config)
                .then(() => {
                    resolve();
                })
                .catch((reason) => {
                    this.handleError(reason);
                    console.error(reason);
                    reject(reason);
                });
        });
    }
}
